import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CodeEditor = ({ 
  title, 
  language, 
  initialCode, 
  onCodeChange, 
  onRun, 
  isRunning = false,
  showOutput = true,
  output = '',
  error = null
}) => {
  const [code, setCode] = useState(initialCode);
  const [theme, setTheme] = useState('dark');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCode(initialCode);
  }, [initialCode]);

  const handleCodeChange = (e) => {
    const newCode = e?.target?.value;
    setCode(newCode);
    if (onCodeChange) {
      onCodeChange(newCode);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard?.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const handleRun = () => {
    if (onRun) {
      onRun(code);
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <div className={`rounded-lg border border-border overflow-hidden ${
      theme === 'dark' ? 'bg-slate-900' : 'bg-white'
    }`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 border-b border-border ${
        theme === 'dark' ? 'bg-slate-800' : 'bg-gray-50'
      }`}>
        <div className="flex items-center space-x-3">
          <div className="flex space-x-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          </div>
          <h3 className={`text-sm font-medium ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            {title}
          </h3>
          <span className={`text-xs px-2 py-1 rounded ${
            theme === 'dark' ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-gray-600'
          }`}>
            {language}
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="xs"
            iconName={theme === 'dark' ? 'Sun' : 'Moon'}
            onClick={toggleTheme}
            className={theme === 'dark' ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}
          />
          <Button
            variant="ghost"
            size="xs"
            iconName={copied ? 'Check' : 'Copy'}
            onClick={handleCopy}
            className={`${copied ? 'text-green-500' : ''} ${
              theme === 'dark' ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          />
          {onRun && (
            <Button
              variant="default"
              size="xs"
              iconName="Play"
              iconPosition="left"
              loading={isRunning}
              onClick={handleRun}
              className="bg-accent hover:bg-hover text-accent-foreground"
            >
              Run
            </Button>
          )}
        </div>
      </div>
      {/* Code Area */}
      <div className="relative">
        <textarea
          value={code}
          onChange={handleCodeChange}
          className={`w-full h-64 p-4 font-mono text-sm resize-none border-none outline-none ${
            theme === 'dark' ?'bg-slate-900 text-slate-100' :'bg-white text-gray-900'
          }`}
          style={{ 
            fontFamily: 'JetBrains Mono, Fira Code, Monaco, Consolas, monospace',
            lineHeight: '1.5',
            tabSize: 2
          }}
          spellCheck={false}
        />
        
        {/* Line numbers overlay */}
        <div className={`absolute left-0 top-0 p-4 pointer-events-none select-none ${
          theme === 'dark' ? 'text-slate-500' : 'text-gray-400'
        }`}>
          {code?.split('\n')?.map((_, index) => (
            <div key={index} className="font-mono text-sm" style={{ lineHeight: '1.5' }}>
              {index + 1}
            </div>
          ))}
        </div>
      </div>
      {/* Output Section */}
      {showOutput && (
        <div className={`border-t border-border ${
          theme === 'dark' ? 'bg-slate-800' : 'bg-gray-50'
        }`}>
          <div className="flex items-center justify-between px-4 py-2">
            <span className={`text-xs font-medium ${
              theme === 'dark' ? 'text-slate-300' : 'text-gray-600'
            }`}>
              Output
            </span>
            {error && (
              <span className="text-xs text-red-500 flex items-center space-x-1">
                <Icon name="AlertCircle" size={12} />
                <span>Error</span>
              </span>
            )}
          </div>
          <div className={`px-4 py-3 font-mono text-sm min-h-16 max-h-32 overflow-y-auto ${
            theme === 'dark' ? 'bg-slate-900 text-slate-100' : 'bg-white text-gray-900'
          }`}>
            {error ? (
              <div className="text-red-500">{error}</div>
            ) : (
              <pre className="whitespace-pre-wrap">{output || 'Click "Run" to see output...'}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CodeEditor;